import type { ClipboardEvent, FormEvent } from 'react';
import { useCallback, useMemo, useState } from 'react';

import { useNavigate } from '@change-corgi/core-react/router';
import { useTracking } from '@change-corgi/core-react/tracking';
import type { TrackingFn } from '@change-corgi/core-react/tracking';

import { useCreateIssueDraft } from '#src/app/pages/homepage/shared/hooks';
import { isLoaded, isLoading } from '#src/app/shared/utils/async';

import { useGenerateSocialPostDraft } from './useGenerateSocialPostDraft';
import type { GenerateAiDraftFromSocialPostErrorReason } from './useGenerateSocialPostDraft/api/api.graphql';
import { useSocialPostPreview } from './useSocialPostPreview';
import type { SocialPostPreviewQuery } from './useSocialPostPreview/api/api.graphql';
import { classifySocialPostInput } from './utils';

export type SocialPostErrorCode = 'unsupported_platform' | 'invalid_link' | 'video_unavailable' | 'generic_error';

type AttachedPost = { url: string; handle: string | undefined };

type SocialPostUrlFormHookResult = ModelHookResult<
	{
		isSubmitting: boolean;
		// True while a tiktok link has been recognized and its metadata preview is still in
		// flight — the UI swaps the textarea for a lightweight loading placeholder during this
		// window rather than showing the chip early.
		isResolvingPreview: boolean;
		error: SocialPostErrorCode | undefined;
		chip: { handle: string | undefined; title: string | undefined; image: string | undefined } | undefined;
	},
	{
		onSubmit: (event: FormEvent<HTMLFormElement>) => void;
		onPaste: (event: ClipboardEvent<HTMLTextAreaElement>) => void;
		onInputChange: () => void;
		onRemoveChip: () => void;
	}
>;

// "We couldn't build a petition from that video" covers every case where we reached the
// video but couldn't turn it into a draft: unavailable/private (SOCIAL_POST_UNAVAILABLE),
// flagged content (FLAGGED_CONTENT), or the model returning no usable draft, e.g. too little
// content (INVALID_JSON_RESPONSE). Genuine transport/HTTP failures fall through to the
// generic "give it another try" message, which is the only retryable-framed one.
const VIDEO_UNAVAILABLE_REASONS: ReadonlySet<string> = new Set<GenerateAiDraftFromSocialPostErrorReason>([
	'SOCIAL_POST_UNAVAILABLE',
	'FLAGGED_CONTENT',
	'INVALID_JSON_RESPONSE',
]);

function mapReasonToErrorCode(reason: string | undefined): SocialPostErrorCode {
	return reason !== undefined && VIDEO_UNAVAILABLE_REASONS.has(reason) ? 'video_unavailable' : 'generic_error';
}

type PreviewSuccess = Extract<SocialPostPreviewQuery['socialPostPreview'], { __typename: 'SocialPostPreviewSuccess' }>;

function getPreviewSuccess(
	preview: SocialPostPreviewQuery['socialPostPreview'] | undefined,
): PreviewSuccess | undefined {
	return preview?.__typename === 'SocialPostPreviewSuccess' ? preview : undefined;
}

// A preview-time UNSUPPORTED_PLATFORM should be unreachable in practice (the client already
// classified the link as a tiktok post before ever attaching it), but it's mapped defensively
// rather than falling through to the generic message in case of client/server drift.
function mapPreviewErrorToCode(preview: SocialPostPreviewQuery['socialPostPreview'] | undefined): SocialPostErrorCode {
	if (preview?.__typename === 'SocialPostPreviewError') {
		return preview.reason === 'UNSUPPORTED_PLATFORM' ? 'unsupported_platform' : 'video_unavailable';
	}
	return 'generic_error';
}

type FreshSubmitContext = {
	track: TrackingFn;
	navigate: ReturnType<typeof useNavigate>;
	isSubmitting: boolean;
	setAttachedPost: (post: AttachedPost) => void;
	setBlockError: (code: SocialPostErrorCode | undefined) => void;
	createIssueDraftMutate: (text: string) => void;
};

// Classifies and routes a submit whose input hasn't already been attached as a post (see
// onSubmit's `attachedPost` branch for the follow-up submit that actually generates once a
// tiktok post's preview has resolved). A detected TikTok post attaches and kicks off its
// preview; a blocked link surfaces its error; freeform text starts an issue draft; empty
// input falls through to the plain SAP intro.
function submitFreshInput(trimmed: string, ctx: FreshSubmitContext): void {
	const classification = classifySocialPostInput(trimmed);

	// A detected TikTok post is the only case that diverges from the SAP hero under the hood
	// (it resolves via generateAiDraftFromSocialPost instead of a plain ai_draft); freeform
	// text — including an empty submit, which also classifies as freeform — takes the exact
	// same path as the SAP hero, so it's tracked under the same event.
	void ctx.track(classification.kind === 'freeform' ? 'homepage_SAP_click' : 'homepage_social_post_click', {
		cta: 'primary',
		prompt: trimmed,
	});

	if (ctx.isSubmitting) return;

	if (!trimmed.length) {
		ctx.navigate('/start-a-petition/intro?source_location=homepage_tiktok');
		return;
	}

	switch (classification.kind) {
		case 'tiktok_post':
			// A link typed by hand: attach it and kick off its preview, same as a pasted link.
			// The user submits again once the chip renders with a resolved preview.
			ctx.setAttachedPost({ url: classification.url, handle: classification.handle });
			break;
		case 'unsupported_platform':
		case 'invalid_link':
			// Gives the click an outcome: the submit was rejected client-side before any
			// generation, so it never reaches the success/error events above.
			ctx.setBlockError(classification.kind);
			void ctx.track('homepage_social_post_blocked', { reason: classification.kind, prompt: trimmed });
			break;
		case 'freeform':
			ctx.createIssueDraftMutate(trimmed);
			break;
		default:
			break;
	}
}

export function useSocialPostUrlForm(): SocialPostUrlFormHookResult {
	const track = useTracking();
	const navigate = useNavigate();

	const generateDraftMutation = useGenerateSocialPostDraft();
	const createIssueDraftMutation = useCreateIssueDraft({ sourceLocation: 'homepage_tiktok' });

	// A detected TikTok post: kicks off its metadata preview immediately, but doesn't show a chip
	// until that preview resolves successfully (see `chip` below).
	const [attachedPost, setAttachedPost] = useState<AttachedPost | undefined>(undefined);
	// Cheap title/image/caption lookup for the attached post. Disabled while no post is attached.
	const previewQuery = useSocialPostPreview(attachedPost?.url);
	// A pre-submit block (unsupported platform / invalid link) surfaced the moment a bad link
	// is pasted; cleared as soon as the user edits the input.
	const [blockError, setBlockError] = useState<SocialPostErrorCode | undefined>(undefined);

	const previewSuccess = getPreviewSuccess(previewQuery.data);
	const previewErrored =
		attachedPost !== undefined && (previewQuery.isError || previewQuery.data?.__typename === 'SocialPostPreviewError');
	const isResolvingPreview = attachedPost !== undefined && previewQuery.isPending;

	const isSubmitting = useMemo(() => {
		if (isLoading(generateDraftMutation) || isLoading(createIssueDraftMutation)) return true;
		if (isLoaded(generateDraftMutation) || isLoaded(createIssueDraftMutation)) return true;
		return false;
	}, [generateDraftMutation, createIssueDraftMutation]);

	const error = useMemo<SocialPostErrorCode | undefined>(() => {
		if (generateDraftMutation.isError) return mapReasonToErrorCode(generateDraftMutation.error.message);
		if (createIssueDraftMutation.isError) return 'generic_error';
		if (previewErrored) return mapPreviewErrorToCode(previewQuery.data);
		return blockError;
	}, [generateDraftMutation, createIssueDraftMutation, previewErrored, previewQuery.data, blockError]);

	const onRemoveChip = useCallback(() => {
		if (attachedPost) {
			// Distinguishes changed-their-mind (dismissed while still neutral, unsubmitted) from
			// gave-up-after-failure (dismissed once the chip had flipped to its error state).
			void track('homepage_social_post_chip_dismissed', { source: attachedPost.url, error: error !== undefined });
		}
		setAttachedPost(undefined);
		generateDraftMutation.reset();
	}, [track, attachedPost, error, generateDraftMutation]);

	const onPaste = useCallback(
		(event: ClipboardEvent<HTMLTextAreaElement>) => {
			const classification = classifySocialPostInput(event.clipboardData.getData('text'));
			if (classification.kind === 'tiktok_post') {
				// Let the URL land in the textarea as usual — a resolving indicator renders below
				// it (see isResolvingPreview) until the preview succeeds, at which point the chip
				// replaces the textarea entirely (see `chip` below).
				setBlockError(undefined);
				// A failed freeform submit would otherwise keep its error banner attached to the new chip.
				if (createIssueDraftMutation.isError) createIssueDraftMutation.reset();
				setAttachedPost({ url: classification.url, handle: classification.handle });
				return;
			}
			if (classification.kind === 'unsupported_platform' || classification.kind === 'invalid_link') {
				// Let the text land in the box, but flag it immediately.
				setBlockError(classification.kind);
			}
		},
		[createIssueDraftMutation],
	);

	const onInputChange = useCallback(() => {
		setBlockError(undefined);
		// A failed preview only ever shows the textarea again once its chip never rendered;
		// clear it so resuming typing starts from a clean slate rather than a stale attachment.
		if (attachedPost && previewErrored) setAttachedPost(undefined);
		// A failed freeform submit's error would otherwise stay visible while the user edits.
		if (createIssueDraftMutation.isError) createIssueDraftMutation.reset();
	}, [attachedPost, previewErrored, createIssueDraftMutation]);

	const onSubmit = useCallback(
		(event: FormEvent<HTMLFormElement>) => {
			event.preventDefault();

			if (attachedPost) {
				void track('homepage_social_post_click', { cta: 'primary', prompt: attachedPost.url });
				// Not yet resolved (still loading, or resolved to an error): nothing to submit yet.
				if (isSubmitting || !previewSuccess) return;
				generateDraftMutation.mutate({ url: attachedPost.url, caption: previewSuccess.caption ?? undefined });
				return;
			}

			const formData = new FormData(event.target as HTMLFormElement);
			const trimmed = ((formData.get('prompt_or_url') as string) || '').trim();
			submitFreshInput(trimmed, {
				track,
				navigate,
				isSubmitting,
				setAttachedPost,
				setBlockError,
				createIssueDraftMutate: createIssueDraftMutation.mutate,
			});
		},
		[track, isSubmitting, attachedPost, previewSuccess, generateDraftMutation, createIssueDraftMutation, navigate],
	);

	return {
		data: {
			isSubmitting,
			isResolvingPreview,
			error,
			chip:
				attachedPost && previewSuccess
					? {
							handle: attachedPost.handle,
							title: previewSuccess.title ?? undefined,
							image: previewSuccess.image ?? undefined,
						}
					: undefined,
		},
		actions: {
			onSubmit,
			onPaste,
			onInputChange,
			onRemoveChip,
		},
	};
}
